'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@libs/core';
const WEB_COLLAGE_SITECODE = 'petsmart';
const WEB_COLLAGE_LOAD_TIMEOUT = 15 * 1000;
const WEB_COLLAGE_SDK_RETRY_INTERVAL = 100;
const WEB_COLLAGE_CONTENT_SETTINGS = {
  'power-page': {
    autoPlayAndStop: true,
    reload: true
  }
};
const WEB_COLLAGE_SUBDOM_SELECTOR = 'syndigo-powerpage'; // HTML tag that Webcollage uses to render the content
const browserGlobal = globalThis as typeof globalThis & Pick<Window, 'flags' | 'Webcollage'>;
type WebcollageAvailability = {
  productId: string;
  found?: boolean;
};
type WebcollageLoad = {
  productId: string;
  started: boolean;
};
type Cleanup = () => void;
type WebcollageSdk = Window['Webcollage'];
function waitForWebcollageSdk(onReady: (sdk: WebcollageSdk) => void, onUnavailable: () => void): Cleanup {
  if (browserGlobal.Webcollage) {
    onReady(browserGlobal.Webcollage);
    return () => undefined;
  }
  let retry: ReturnType<typeof setInterval>;
  let timeout: ReturnType<typeof setTimeout>;
  const cleanup = () => {
    clearInterval(retry);
    clearTimeout(timeout);
  };
  const checkSdk = () => {
    if (!browserGlobal.Webcollage) return;
    cleanup();
    onReady(browserGlobal.Webcollage);
  };
  retry = setInterval(checkSdk, WEB_COLLAGE_SDK_RETRY_INTERVAL);
  timeout = setTimeout(() => {
    cleanup();
    onUnavailable();
  }, WEB_COLLAGE_LOAD_TIMEOUT);
  return cleanup;
}
function watchForWebcollageContent(target: Element, onFound: () => void, onUnavailable: () => void): Cleanup {
  let timeout: ReturnType<typeof setTimeout>;
  const observer = new MutationObserver(detectContent);
  function cleanup() {
    clearTimeout(timeout);
    observer.disconnect();
  }
  function detectContent() {
    if (!target.querySelector(WEB_COLLAGE_SUBDOM_SELECTOR)) return;
    cleanup();
    onFound();
  }
  observer.observe(target, {
    childList: true,
    subtree: true
  });
  timeout = setTimeout(onUnavailable, WEB_COLLAGE_LOAD_TIMEOUT);
  detectContent();
  return cleanup;
}
export function useWebcollage(productId) {
  const $ = _c(12);
  let t0;
  if ($[0] !== productId) {
    t0 = {
      productId
    };
    $[0] = productId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [availability, setAvailability] = useState(t0);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const currentLoad = useRef(undefined);
  const webCollageFound = availability.productId === productId ? availability.found : undefined;
  let t1;
  if ($[2] !== productId) {
    t1 = () => {
      const load = currentLoad.current;
      if (load?.productId !== productId || load?.started) {
        return;
      }
      setInitializationAttempt(_temp);
    };
    $[2] = productId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const initializeWebcollage = t1;
  let t2;
  if ($[4] !== productId) {
    t2 = () => {
      const cleanups = new Set();
      const target = document.querySelector("#wc-power-page");
      target?.replaceChildren();
      if (browserGlobal.flags?.IS_PERFORMANCE_MODE) {
        setAvailability({
          productId,
          found: false
        });
        return;
      }
      if (!target) {
        setAvailability({
          productId,
          found: false
        });
        return;
      }
      const load_0 = {
        productId,
        started: false
      };
      currentLoad.current = load_0;
      setAvailability({
        productId
      });
      const loadProductContent = sdk => {
        load_0.started = true;
        const stopWatching = watchForWebcollageContent(target, () => setAvailability({
          productId,
          found: true
        }), () => setAvailability({
          productId,
          found: false
        }));
        cleanups.add(stopWatching);
        ;
        try {
          sdk.loadProductContent(WEB_COLLAGE_SITECODE, String(productId), WEB_COLLAGE_CONTENT_SETTINGS);
        } catch (t3) {
          const error = t3;
          load_0.started = false;
          stopWatching();
          logger.error("[useSalsify] Failed to load Webcollage content", error);
          setAvailability({
            productId,
            found: false
          });
        }
      };
      cleanups.add(waitForWebcollageSdk(loadProductContent, () => setAvailability({
        productId,
        found: false
      })));
      return () => {
        if (currentLoad.current === load_0) {
          currentLoad.current = undefined;
        }
        cleanups.forEach(_temp2);
      };
    };
    $[4] = productId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== initializationAttempt || $[7] !== productId) {
    t3 = [initializationAttempt, productId];
    $[6] = initializationAttempt;
    $[7] = productId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== initializeWebcollage || $[10] !== webCollageFound) {
    t4 = {
      initializeWebcollage,
      webCollageFound
    };
    $[9] = initializeWebcollage;
    $[10] = webCollageFound;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp2(cleanup) {
  return cleanup();
}
function _temp(attempt) {
  return attempt + 1;
}